// src/pages/PrivacyPolicy.tsx
import React from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft, ExternalLink } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* Header */}
      <header className="border-b border-[#e2e8f0] px-6 py-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-gray-500 hover:text-[#0f172a] transition">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-[#a78bfa] rounded-full" />
            <span className="text-sm text-gray-500">Input</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-6">
            <Shield className="w-12 h-12 text-[#a78bfa]" />
            <h1 className="text-4xl md:text-5xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-gray-500 text-sm">
            Effective Date: <strong>19 November 2025</strong> | Last Updated: <strong>24 March 2026</strong>
          </p>
        </div>

        <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-8 md:p-12 space-y-10 text-gray-600 leading-relaxed">
          {/* COMPANY INFO */}
          <section>
            <p className="font-medium"><strong>Company:</strong> Input Performance Ltd ("we", "our", "us")</p>
            <p>Registered in England and Wales</p>
            <p>ICO Registration Number: ZC036976</p>
            <p>Registered Office: 5 Kympton Gardens, GU34 5JL</p>
            <p className="mt-4">
              <strong>Email:</strong>{" "}
              <a href="mailto:privacy@inputperformanceai.com" className="text-[#a78bfa] hover:underline">
                privacy@inputperformanceai.com
              </a>
            </p>
          </section>

          {/* 1. INTRODUCTION */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">1. Introduction</h2>
            <p>Input Performance Ltd provides metabolic testing, performance analytics, AI-based coaching, and corporate wellbeing optimisation services.</p>
            <p className="mt-4">
              This Privacy Policy explains how we collect, use, store, and protect your personal data in line with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
            <p className="mt-4">We act as the data controller for personal data collected through:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Our website and online booking systems</li>
              <li>Our mobile application (iOS and Android)</li>
              <li>In-app AI coaching system</li>
              <li>Wearable integrations (Garmin, WHOOP, Apple Health)</li>
              <li>Social features (friends, challenges, leaderboards)</li>
              <li>Corporate wellness programs</li>
              <li>Email communication and digital forms</li>
            </ul>
            <p className="mt-6">
              If you have any questions, contact our Data Protection Lead:<br />
              <strong>privacy@inputperformanceai.com</strong>
            </p>
          </section>

          {/* 2. DATA WE COLLECT */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">2. Data We Collect</h2>
            <p>We collect the following categories of data:</p>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">A. Personal Identification Data</h3>
            <ul className="list-disc pl-8 space-y-1">
              <li>Name, email, phone number</li>
              <li>Date of birth, gender</li>
              <li>Address (if provided)</li>
              <li>Emergency contact details (if provided)</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">B. Communication Data</h3>
            <ul className="list-disc pl-8 space-y-1">
              <li>In-app chat messages, images, and voice notes</li>
              <li>AI-generated responses and your interactions</li>
              <li>Email correspondence</li>
              <li>Feedback forms and survey submissions</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">C. Health & Performance Data (Special Category Data)</h3>
            <p>We only process this data with your explicit consent:</p>
            <ul className="list-disc pl-8 space-y-1 mt-4">
              <li>Resting Metabolic Rate (RMR), VO2 max, HRV</li>
              <li>Heart rate zones and recovery data</li>
              <li>Body composition, weight, body fat %</li>
              <li>Fitness test data and metabolic reports</li>
              <li>Personal best (PB) records including:</li>
              <li className="ml-12">Strength metrics (back squat, front squat, deadlift, bench press, overhead press, snatch, clean & jerk)</li>
              <li className="ml-12">Endurance times (1km, 5km, 10km, half marathon, marathon)</li>
              <li className="ml-12">Cycling metrics (FTP, 5-min power, 20-min power)</li>
              <li className="ml-12">Body composition metrics (body fat %, lean mass, bodyweight)</li>
              <li className="ml-12">Sport-specific metrics (VO2 max, Fran, Murph, Grace, HYROX, triathlon times)</li>
              <li>Wearable data from Garmin and WHOOP, including:</li>
              <li className="ml-12">Sleep, HRV, strain, workouts</li>
              <li className="ml-12">Step count, heart rate, movement metrics</li>
              <li className="ml-12">Recovery and performance insights</li>
              <li>Hydration data (daily water intake, hydration goals based on body weight)</li>
              <li>Connected equipment data from Concept2 rowing machines via Bluetooth, including:</li>
              <li className="ml-12">Stroke rate, pace, distance, and power output</li>
              <li className="ml-12">Heart rate data during rowing sessions</li>
              <li className="ml-12">Interval and session structure data</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">D. Nutrition & Meal Planning Data</h3>
            <ul className="list-disc pl-8 space-y-1">
              <li>Nutritional preferences and allergies</li>
              <li>Diet style (e.g., Mediterranean, Keto, Vegan)</li>
              <li>Food intolerances and absolute dislikes</li>
              <li>Preferred cuisines</li>
              <li>Cooking skill level and available kitchen equipment</li>
              <li>Budget preferences</li>
              <li>Meal variety and prep preferences</li>
              <li>Lunch style (batch prep, grab-and-go, full recipe)</li>
              <li>Work schedule (office, remote, shift, student, flexible)</li>
              <li>Dietary focus (balanced, high protein, low carb, whole foods, performance)</li>
              <li>Meal times and snack preferences</li>
              <li>Meal logs and calorie/macro tracking</li>
              <li>Barcode scan data and food product lookups</li>
              <li>Food photos and images uploaded for meal logging</li>
              <li>Supplement names, dosages, and daily supplement logs</li>
              <li>Supplement barcode scans for product identification</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">E. Training Program Data</h3>
            <ul className="list-disc pl-8 space-y-1">
              <li>Training program preferences (sport, duration, target metrics)</li>
              <li>Primary and secondary sports</li>
              <li>Scheduled workouts and workout sections</li>
              <li>Workout completion status and notes</li>
              <li>Training load and volume data</li>
              <li>Synced workout data from connected devices</li>
              <li>Week-by-week progression and periodisation</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">F. Social & Community Data</h3>
            <p>If you use our social features, we collect:</p>
            <ul className="list-disc pl-8 space-y-1 mt-4">
              <li>Friend connections and friend requests (sent and received)</li>
              <li>Challenge participation and scores</li>
              <li>Leaderboard rankings</li>
              <li>Challenge invitations you send or receive</li>
            </ul>
            <p className="mt-4"><strong>Data Visibility:</strong></p>
            <ul className="list-disc pl-8 space-y-1">
              <li>Your name and profile may be visible to friends you connect with</li>
              <li>Challenge leaderboards show participant names and scores to other participants</li>
              <li>You control who you connect with and which challenges you join</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">G. AI Memory & Personalisation Data</h3>
            <p>To provide personalised coaching, we store:</p>
            <ul className="list-disc pl-8 space-y-1 mt-4">
              <li>Key facts about your preferences, lifestyle, and goals ("Coach Memory")</li>
              <li>Conversation embeddings for context-aware AI responses</li>
              <li>Your preferred communication tone (Direct, Motivational, Scientific, Supportive)</li>
              <li>Recent conversation history for continuity</li>
            </ul>
            <p className="mt-4">This data helps our AI remember important context about you across conversations, enabling more relevant and personalised coaching.</p>
            <p className="mt-4"><strong>Your Control:</strong></p>
            <ul className="list-disc pl-8 space-y-1">
              <li>You can request deletion of coach memory at any time</li>
              <li>Memory can be cleared via your dashboard or by emailing privacy@inputperformanceai.com</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">H. Payment & Transaction Data</h3>
            <ul className="list-disc pl-8 space-y-1">
              <li>Subscription and payment information</li>
              <li>Subscription plan type (monthly, yearly) and status</li>
              <li>Subscription provider (Apple App Store or Stripe)</li>
              <li>Apple in-app purchase transaction identifiers</li>
              <li>Invoices, receipts, transaction history</li>
              <li>Processed securely via Stripe and/or Apple (we do not store full card details)</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">I. Device, Analytics, and Tracking Data</h3>
            <ul className="list-disc pl-8 space-y-1">
              <li>IP address, browser type, device type</li>
              <li>Device identifiers for authentication and session management</li>
              <li>Push notification tokens (if notifications are enabled)</li>
              <li>App crash reports and diagnostic data</li>
              <li>Product analytics and feature usage events (collected via PostHog, hosted in the EU)</li>
              <li>Session information and in-app navigation patterns</li>
              <li>Authentication tokens and session data stored locally on your device</li>
              <li>Cookies and tracking data (see Section 9A below)</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">J. Photos & Media Data</h3>
            <p>If you use camera or photo features, we may collect:</p>
            <ul className="list-disc pl-8 space-y-1 mt-4">
              <li>Photos taken or selected for meal logging and AI food scanning</li>
              <li>Barcode images captured via the in-app camera (processed locally on your device to extract product codes)</li>
              <li>Workout share card images generated from your performance data</li>
            </ul>
            <p className="mt-4">When you use the AI food scanner, your photo is sent securely to our AI provider (Anthropic) for analysis and is not permanently stored. Only the resulting nutritional estimates are saved if you choose to log them. Barcode data is processed on your device and only the extracted product code is sent to our food database providers for lookup. Workout share cards are saved to your device photo library only with your permission and are shared externally only when you choose to do so.</p>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">K. Habit Tracking Data</h3>
            <p>If you use our habit tracking features, we collect:</p>
            <ul className="list-disc pl-8 space-y-1 mt-4">
              <li>Custom habit definitions (name, category, frequency, timing)</li>
              <li>Habit categories: health, fitness, mindset, and recovery</li>
              <li>Daily and weekly habit completion logs</li>
              <li>Habit streak data and milestone progress</li>
            </ul>
            <p className="mt-4">Habit data is used to provide personalised coaching insights, streak tracking, and progress summaries. It is not shared with third parties.</p>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">L. Device Permissions & Sensor Data</h3>
            <p>Our app may request access to the following device capabilities:</p>
            <ul className="list-disc pl-8 space-y-1 mt-4">
              <li><strong>Camera:</strong> Used for barcode scanning (food products and supplements). Images are processed locally on your device.</li>
              <li><strong>Photo Library:</strong> Used to save workout share card images to your device. Access is requested only when you choose to save or share.</li>
              <li><strong>Bluetooth:</strong> Used to connect to Concept2 rowing machines for live workout data. Bluetooth scanning may require location permission on Android (see below).</li>
              <li><strong>Location (Android only):</strong> Required by Android for Bluetooth Low Energy (BLE) device scanning. We do not collect, store, or transmit your geographic location. This permission is used solely to enable Bluetooth connectivity with fitness equipment.</li>
              <li><strong>Microphone (Android only):</strong> May be requested by the operating system for certain device interactions. We do not record, store, or transmit audio data.</li>
            </ul>
            <p className="mt-4">You can manage all device permissions through your device's system settings at any time. Denying a permission will only disable the specific feature that requires it.</p>
          </section>

          {/* 3. HOW WE COLLECT */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">3. How We Collect Your Data</h2>
            <p>We collect data when:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>You sign up or create an account</li>
              <li>You message our AI coach in the app</li>
              <li>You book a service or complete a form</li>
              <li>You connect a wearable device and grant access</li>
              <li>You create or join challenges with other users</li>
              <li>You send or accept friend requests</li>
              <li>You log meals or nutrition data</li>
              <li>You create or follow a training program</li>
              <li>You participate in corporate wellbeing assessments</li>
              <li>You interact with our website or marketing content</li>
              <li>We automatically receive analytics and cookie data</li>
              <li>You scan a food barcode using the in-app camera</li>
              <li>You upload or take photos for meal logging</li>
              <li>You connect a Concept2 rowing machine via Bluetooth</li>
              <li>You create or complete habits in the habit tracker</li>
              <li>You log supplements or scan supplement barcodes</li>
              <li>You log hydration or water intake</li>
            </ul>
          </section>

          {/* 3A. AI CHAT CONSENT */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">3A. AI Chat Consent</h2>
            <p>When you verify your phone number, you provide explicit consent for Input Performance Ltd to provide AI coaching through the in-app chat for service-related purposes. Your data is shared with Anthropic (Claude) to power AI coaching responses. This includes:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Account verification</li>
              <li>Coaching messages</li>
              <li>Daily or weekly reminders (if enabled)</li>
              <li>Nutrition, training, and performance insights</li>
              <li>Important service updates</li>
              <li>Customer support</li>
            </ul>
            <p className="mt-4">These messages are considered service messages, not marketing.<br />
            We will not send marketing messages without additional explicit opt-in.</p>
            <p className="mt-4">You may withdraw consent at any time by:</p>
            <ul className="list-disc pl-8 space-y-2">
              <li>Disabling chat in Settings</li>
              <li>Turning off automations in your dashboard</li>
              <li>Emailing privacy@inputperformanceai.com</li>
            </ul>
            <p>When consent is withdrawn, all AI coaching messages stop immediately.</p>
          </section>

          {/* 4. WEARABLE CONSENT */}
          <section className="space-y-10">
            <h2 className="text-2xl font-bold text-[#0f172a] mb-6">4. Wearable Device Data Consent</h2>

            {/* 4A. GARMIN */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-[#0f172a]">4A. Garmin Data Use, Storage, and Deletion</h3>
              <p className="text-gray-600">When you connect a Garmin device, you explicitly consent to the transfer of your Garmin Health data to Input Performance Ltd.</p>
              <p className="text-gray-600">We only access the categories of Garmin data that you grant permission for during the Garmin Connect authorisation process. This may include:</p>
              <ul className="list-disc pl-8 space-y-1 text-gray-600">
                <li>Activity summaries (steps, distance, calories, intensity minutes)</li>
                <li>Heart rate and resting heart rate</li>
                <li>HRV summary values</li>
                <li>Sleep summaries and sleep stages</li>
                <li>Stress metrics</li>
                <li>Body Battery</li>
                <li>Recovery-related physiological metrics</li>
                <li>Workout summaries (duration, HR, distance, calories)</li>
              </ul>
              <p className="text-gray-600"><strong>We do not collect or store raw Garmin files (such as FIT, TCX, or GPX files).</strong></p>
              <p className="text-gray-600">We use Garmin data only to provide performance insights, recovery summaries, training recommendations, and personalised coaching. We apply strict data minimisation and only store the summary metrics required for these insights.</p>
              <h4 className="font-semibold text-[#0f172a] mt-6">Revoking Garmin Access</h4>
              <p className="text-gray-600">You may revoke Garmin access at any time via your Garmin Connect settings or by contacting us at <a href="mailto:privacy@inputperformanceai.com" className="text-green-400 underline">privacy@inputperformanceai.com</a>.</p>
              <p className="text-gray-600">Once revoked, we immediately stop receiving all future Garmin data.<br />All Garmin-derived data associated with your account will be deleted within 30 days of revocation unless you explicitly request immediate deletion.</p>
              <h4 className="font-semibold text-[#0f172a] mt-4">Exporting Your Garmin Data</h4>
              <p className="text-gray-600">You may export all Garmin-derived data at any time through the Input user dashboard.<br />Exports include only the data you generated and authorised us to access.</p>
              <h4 className="font-semibold text-[#0f172a] mt-4">Data Sharing & Retention</h4>
              <p className="text-gray-600">We do not share Garmin data with any third parties.<br />Garmin data is never sold, used for marketing, or disclosed outside Input Performance Ltd.<br />Garmin data is retained only for as long as your account remains connected and active.</p>
            </div>

            {/* 4B. WHOOP */}
            <div className="space-y-4 pt-8 border-t border-[#e2e8f0]">
              <h3 className="text-xl font-semibold text-[#0f172a]">4B. WHOOP Data Use, Storage, and Deletion</h3>
              <p className="text-gray-600">When you connect a WHOOP device, you explicitly consent to the transfer of your WHOOP data to Input Performance Ltd through the WHOOP Developer API.</p>
              <p className="text-gray-600">We only access the categories of WHOOP data that you authorise during the WHOOP OAuth connection flow. Depending on your device settings, this may include:</p>
              <h4 className="font-semibold text-[#0f172a]">WHOOP Sleep Data</h4>
              <ul className="list-disc pl-8 space-y-1 text-gray-600">
                <li>Sleep start and end times</li>
                <li>Sleep duration</li>
                <li>Sleep stages summary</li>
                <li>Respiratory rate</li>
                <li>Sleep efficiency</li>
                <li>Sleep performance</li>
                <li>Sleep consistency</li>
                <li>Sleep disturbances (summaries only)</li>
              </ul>
              <h4 className="font-semibold text-[#0f172a] mt-4">WHOOP Recovery Data</h4>
              <ul className="list-disc pl-8 space-y-1 text-gray-600">
                <li>Recovery score</li>
                <li>Heart-rate variability (HRV)</li>
                <li>Resting heart rate</li>
                <li>Skin temperature</li>
                <li>Respiratory rate</li>
                <li>Strain-related insights depending on WHOOP's scoring output</li>
              </ul>
              <h4 className="font-semibold text-[#0f172a] mt-4">WHOOP Activity & Workout Data</h4>
              <ul className="list-disc pl-8 space-y-1 text-gray-600">
                <li>Activity start and end times</li>
                <li>Workout type</li>
                <li>Heart rate & max HR</li>
                <li>Average HR</li>
                <li>Strain score</li>
                <li>Estimated energy expenditure (kJ → kcal)</li>
                <li>Distance & elevation (if provided)</li>
              </ul>
              <h4 className="font-semibold text-[#0f172a] mt-4">Data We Do NOT Collect</h4>
              <ul className="list-disc pl-8 space-y-1 text-gray-600">
                <li>Raw WHOOP sensor data</li>
                <li>Any WHOOP data outside the scopes you explicitly grant</li>
                <li>Any user-identifying data not provided by WHOOP</li>
                <li>Any private WHOOP profile details that WHOOP does not expose via API</li>
              </ul>
              <p className="text-gray-600">We do not collect, store, or process any raw WHOOP file formats (e.g., CSV, FIT, JSON exports, or internal WHOOP diagnostics).</p>
              <h4 className="font-semibold text-[#0f172a] mt-4">How We Use WHOOP Data</h4>
              <p className="text-gray-600">WHOOP data is used exclusively to deliver:</p>
              <ul className="list-disc pl-8 space-y-1 text-gray-600">
                <li>Daily recovery summaries</li>
                <li>Sleep-based insights</li>
                <li>Training recommendations</li>
                <li>Readiness scoring</li>
                <li>Post-workout nutritional guidance</li>
                <li>Performance trend analysis over time</li>
              </ul>
              <p className="text-gray-600">Your WHOOP data is never used for marketing, never sold, and never shared with third parties.</p>
              <h4 className="font-semibold text-[#0f172a] mt-4">Revoking WHOOP Access</h4>
              <p className="text-gray-600">You may revoke WHOOP access at any time by:</p>
              <ul className="list-disc pl-8 space-y-1 text-gray-600">
                <li>Disconnecting your WHOOP device in the WHOOP App</li>
                <li>Revoking Input in your WHOOP account settings</li>
                <li>Contacting <a href="mailto:privacy@inputperformanceai.com" className="text-green-400 underline">privacy@inputperformanceai.com</a></li>
                <li>Using the "Disconnect WHOOP Access" option in your Input dashboard</li>
              </ul>
              <p className="text-gray-600">Revoking access stops all future WHOOP data imports immediately.<br />All WHOOP-derived data is deleted within 30 days (or immediately upon request).</p>
              <h4 className="font-semibold text-[#0f172a] mt-4">Exporting Your WHOOP Data</h4>
              <p className="text-gray-600">You may export all WHOOP-derived data at any time through your Input user dashboard.</p>
            </div>

            {/* WEARABLE DATA RIGHTS SUMMARY */}
            <div className="bg-[#f8fafc] border border-green-500/30 rounded-xl p-6 mt-10">
              <h3 className="text-lg font-bold text-green-400 mb-3">Your Data Rights — Summary</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>You control exactly what wearable data we access</li>
                <li>You can revoke access anytime — we stop receiving data immediately</li>
                <li>All wearable data is deleted within 30 days of disconnection</li>
                <li>You can export your data anytime</li>
                <li>We never sell, share, or use your data for marketing</li>
              </ul>
            </div>

            {/* 4C. CONNECTED FITNESS EQUIPMENT */}
            <div className="space-y-4 pt-8 border-t border-[#e2e8f0]">
              <h3 className="text-xl font-semibold text-[#0f172a]">4C. Connected Fitness Equipment (Concept2 Rower)</h3>
              <p className="text-gray-600">When you connect a Concept2 rowing machine via Bluetooth, you consent to the transfer of workout data from the device to our app. This data is collected in real time during your rowing session and may include:</p>
              <ul className="list-disc pl-8 space-y-1 text-gray-600">
                <li>Stroke rate and stroke count</li>
                <li>Pace (split time per 500m)</li>
                <li>Distance rowed</li>
                <li>Power output (watts)</li>
                <li>Heart rate (if a heart rate monitor is connected to the rower)</li>
                <li>Interval and session duration</li>
              </ul>
              <p className="text-gray-600">Concept2 data is collected via Bluetooth Low Energy (BLE) directly from the rowing machine's performance monitor. No data is sent to Concept2 or any third party — all data stays within Input.</p>
              <h4 className="font-semibold text-[#0f172a] mt-4">Disconnecting</h4>
              <p className="text-gray-600">You can disconnect your Concept2 device at any time by ending the Bluetooth connection in the app or through your device's Bluetooth settings. Historical workout data from Concept2 sessions is retained as part of your training log until you request deletion.</p>
            </div>
          </section>

          {/* 5. LEGAL BASES */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">5. Legal Bases for Processing</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left border border-gray-700 text-sm">
                <thead>
                  <tr className="bg-[#f1f5f9]">
                    <th className="p-4 border border-gray-700">Purpose</th>
                    <th className="p-4 border border-gray-700">Legal Basis</th>
                    <th className="p-4 border border-gray-700">Examples</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="p-4 border border-gray-700">Delivering our services</td><td className="p-4 border border-gray-700">Performance of a Contract</td><td className="p-4 border border-gray-700">Creating personalised coaching plans</td></tr>
                  <tr className="bg-[#f8fafc]"><td className="p-4 border border-gray-700">Communication via in-app chat/email</td><td className="p-4 border border-gray-700">Legitimate Interests</td><td className="p-4 border border-gray-700">Providing support, updates, or reminders</td></tr>
                  <tr><td className="p-4 border border-gray-700">Processing health data</td><td className="p-4 border border-gray-700">Explicit Consent</td><td className="p-4 border border-gray-700">Metabolic testing, wearable data analysis</td></tr>
                  <tr className="bg-[#f8fafc]"><td className="p-4 border border-gray-700">Payment processing</td><td className="p-4 border border-gray-700">Legal Obligation / Contract</td><td className="p-4 border border-gray-700">Subscriptions, invoices, HMRC compliance</td></tr>
                  <tr><td className="p-4 border border-gray-700">Marketing communications</td><td className="p-4 border border-gray-700">Consent</td><td className="p-4 border border-gray-700">Email marketing opt-ins</td></tr>
                  <tr className="bg-[#f8fafc]"><td className="p-4 border border-gray-700">Platform security & compliance</td><td className="p-4 border border-gray-700">Legal Obligation</td><td className="p-4 border border-gray-700">Fraud prevention, safety measures</td></tr>
                  <tr><td className="p-4 border border-gray-700">Social features (friends, challenges)</td><td className="p-4 border border-gray-700">Consent</td><td className="p-4 border border-gray-700">You choose to add friends and join challenges</td></tr>
                  <tr className="bg-[#f8fafc]"><td className="p-4 border border-gray-700">AI personalisation & memory</td><td className="p-4 border border-gray-700">Legitimate Interests / Consent</td><td className="p-4 border border-gray-700">Remembering preferences for better coaching</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* 6. HOW WE USE YOUR DATA */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">6. How We Use Your Data</h2>
            <p>We use your data to:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Provide personalised metabolic, nutrition, and training guidance</li>
              <li>Generate AI-based insights and performance reports</li>
              <li>Communicate with you via in-app chat, email, or push notification</li>
              <li>Manage your account, bookings, and subscriptions</li>
              <li>Integrate and analyse wearable device data</li>
              <li>Create and manage AI-generated nutrition and training plans</li>
              <li>Enable social features including friends, challenges, and leaderboards</li>
              <li>Generate workout share cards from your performance data that you can save or share externally</li>
              <li>Store AI memory to provide contextual, personalised coaching</li>
              <li>Improve the quality and accuracy of our services</li>
              <li>Provide aggregated corporate wellbeing reports</li>
              <li>Meet legal and regulatory requirements</li>
              <li>Deliver personalised coaching and performance insights through the app (with consent)</li>
            </ul>
            <p className="font-medium mt-6">We never sell your data.</p>
          </section>

          {/* 6A. NO MARKETING WITHOUT CONSENT */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">6A. No Marketing Without Consent</h2>
            <p>We do not send marketing or promotional content via the app unless you provide a separate and explicit marketing opt-in. All messages sent by our AI coach are service or support messages only.</p>
          </section>

          {/* 6B. WEARABLE DATA & AI COACHING */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">6B. Wearable Data and AI Coaching Messages</h2>
            <p>When enabled, wearable data may be used to generate personalised AI coaching messages (e.g., sleep insights, training recovery, workout analysis). This functionality is optional, requires consent, and can be disabled at any time.</p>
          </section>

          {/* 7. SHARING */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">7. Sharing Your Data</h2>
            <p>We only share data with trusted service providers necessary for delivering our services:</p>
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-left border border-gray-700 text-sm">
                <thead>
                  <tr className="bg-[#f1f5f9]">
                    <th className="p-4 border border-gray-700">Provider</th>
                    <th className="p-4 border border-gray-700">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="p-4 border border-gray-700">Supabase</td><td className="p-4 border border-gray-700">Secure database hosting & backend operations</td></tr>
                  <tr><td className="p-4 border border-gray-700">Anthropic</td><td className="p-4 border border-gray-700">AI-powered nutrition plan generation, training program generation & analysis</td></tr>
                  <tr><td className="p-4 border border-gray-700">Stripe</td><td className="p-4 border border-gray-700">Secure payments & subscription management</td></tr>
                  <tr className="bg-[#f8fafc]"><td className="p-4 border border-gray-700">Apple (App Store / StoreKit)</td><td className="p-4 border border-gray-700">In-app purchase processing and subscription management on iOS</td></tr>
                  <tr><td className="p-4 border border-gray-700">Garmin, WHOOP</td><td className="p-4 border border-gray-700">Wearable data imports</td></tr>
                  <tr><td className="p-4 border border-gray-700">PostHog</td><td className="p-4 border border-gray-700">Product analytics and feature usage tracking (hosted in the EU for GDPR compliance)</td></tr>
                  <tr className="bg-[#f8fafc]"><td className="p-4 border border-gray-700">Open Food Facts</td><td className="p-4 border border-gray-700">Public food and nutrition database used for barcode scanning and food lookups</td></tr>
                  <tr><td className="p-4 border border-gray-700">Edamam</td><td className="p-4 border border-gray-700">Nutrition data API used for food search and nutritional analysis</td></tr>
                </tbody>
              </table>
            </div>
            <p className="mt-6">All third parties are bound by Data Processing Agreements and comply with GDPR and approved international transfer mechanisms.<br />
            We do not share your data with employers unless aggregated and anonymised.</p>
          </section>

          {/* 8. RETENTION */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">8. Data Retention</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left border border-gray-700 text-sm">
                <thead>
                  <tr className="bg-[#f1f5f9]">
                    <th className="p-4 border border-gray-700">Data Type</th>
                    <th className="p-4 border border-gray-700">Retention Period</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="p-4 border border-gray-700">Client records & reports</td><td className="p-4 border border-gray-700">6 years after last activity</td></tr>
                  <tr className="bg-[#f8fafc]"><td className="p-4 border border-gray-700">AI coach conversations</td><td className="p-4 border border-gray-700">Up to 24 months</td></tr>
                  <tr><td className="p-4 border border-gray-700">Wearable data</td><td className="p-4 border border-gray-700">Until you revoke access (deleted within 30 days)</td></tr>
                  <tr className="bg-[#f8fafc]"><td className="p-4 border border-gray-700">Payment & transaction data</td><td className="p-4 border border-gray-700">7 years (HMRC requirement)</td></tr>
                  <tr><td className="p-4 border border-gray-700">Marketing data</td><td className="p-4 border border-gray-700">Until you unsubscribe</td></tr>
                  <tr className="bg-[#f8fafc]"><td className="p-4 border border-gray-700">AI logs & interactions</td><td className="p-4 border border-gray-700">Up to 12 months</td></tr>
                  <tr><td className="p-4 border border-gray-700">AI advice generation logs</td><td className="p-4 border border-gray-700">Up to 12 months</td></tr>
                  <tr className="bg-[#f8fafc]"><td className="p-4 border border-gray-700">Coach memory & personalisation</td><td className="p-4 border border-gray-700">Until you request deletion or close account</td></tr>
                  <tr><td className="p-4 border border-gray-700">Conversation embeddings</td><td className="p-4 border border-gray-700">Up to 12 months</td></tr>
                  <tr className="bg-[#f8fafc]"><td className="p-4 border border-gray-700">Challenge data & scores</td><td className="p-4 border border-gray-700">12 months after challenge ends</td></tr>
                  <tr><td className="p-4 border border-gray-700">Friend connections</td><td className="p-4 border border-gray-700">Until you remove the connection</td></tr>
                  <tr className="bg-[#f8fafc]"><td className="p-4 border border-gray-700">Nutrition plans & meal logs</td><td className="p-4 border border-gray-700">Until you delete or close account</td></tr>
                  <tr><td className="p-4 border border-gray-700">Training programs & workouts</td><td className="p-4 border border-gray-700">Until you delete or close account</td></tr>
                  <tr className="bg-[#f8fafc]"><td className="p-4 border border-gray-700">Personal bests (PBs)</td><td className="p-4 border border-gray-700">Until you delete or close account</td></tr>
                </tbody>
              </table>
            </div>
            <p className="mt-6">You may request deletion at any time unless legal retention applies.</p>
          </section>

          {/* 9. SECURITY */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">9. Data Security</h2>
            <p>We apply multiple safeguards:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Encrypted connections (HTTPS, SSL)</li>
              <li>Row-Level Security (RLS) in our Supabase database — every piece of data is locked to your specific account and cannot be accessed by any other user</li>
              <li>Security audits and monitoring</li>
              <li>Secure API key handling</li>
              <li>Encrypted cloud storage</li>
              <li>Staff training and confidentiality agreements</li>
            </ul>
          </section>

          {/* 9A. COOKIES & ANALYTICS */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">9A. Cookies & Analytics</h2>
            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">Website Cookies</h3>
            <p className="mt-2">Our website may use the following types of cookies:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li><strong>Essential cookies:</strong> Required for authentication, session management, and security. These cannot be disabled.</li>
              <li><strong>Analytics cookies:</strong> Help us understand how visitors interact with our website. These can be managed through your browser settings.</li>
              <li><strong>Preference cookies:</strong> Remember your settings and choices (e.g., theme preferences).</li>
            </ul>
            <p className="mt-4">We do not use third-party advertising or retargeting cookies. You can manage cookie preferences through your browser settings. Disabling essential cookies may affect your ability to use our services.</p>
            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">Mobile App Analytics</h3>
            <p className="mt-2">Our mobile app uses PostHog (hosted in the EU at eu.posthog.com) to collect product analytics. This includes:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Feature usage events (e.g., which screens you visit, which features you use)</li>
              <li>Onboarding progress and completion</li>
              <li>Workout and meal logging activity (aggregated, not content)</li>
              <li>Subscription events and plan changes</li>
              <li>Error events for service improvement</li>
              <li>Device type, operating system, and app version</li>
            </ul>
            <p className="mt-4">Analytics data is used solely to improve our services and user experience. We do not use analytics data for advertising or sell it to third parties. PostHog's EU hosting ensures your analytics data is processed within the European Economic Area.</p>
          </section>

          {/* 10. INTERNATIONAL TRANSFERS */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">10. International Data Transfers</h2>
            <p>Some processors operate outside the UK (e.g.,Anthropic, Supabase).<br />
            We ensure safe transfers using:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Standard Contractual Clauses (SCCs)</li>
              <li>UK GDPR Addendum</li>
              <li>GDPR-compliant transfer mechanisms</li>
              <li>Vendors with ISO 27001 / SOC 2 accreditation</li>
              <li>Anthropic (US-based AI processing and coaching, nutrition plans)</li>
              <li>Apple Inc. (US-based in-app purchase and subscription processing)</li>
              <li>Open Food Facts and Edamam (food database queries may be processed internationally)</li>
              <li>PostHog (EU-hosted product analytics — data processed within the European Economic Area)</li>
              <li>Terra API (health and nutrition data aggregation for wearable and nutrition app integrations)</li>
            </ul>
          </section>

          {/* 11. YOUR RIGHTS */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">11. Your Data Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Access your data</li>
              <li>Rectify inaccurate information</li>
              <li>Erase your data ("right to be forgotten")</li>
              <li>Manage your data export and data deletion at any time through your online account via Settings → Data Export & Account</li>
              <li>Restrict processing</li>
              <li>Object to processing</li>
              <li>Data portability</li>
              <li>Withdraw consent (especially for health data)</li>
              <li>Request deletion of AI coach memory and personalisation data</li>
              <li>Clear conversation embeddings used for context</li>
              <li>Remove friend connections and leave challenges</li>
            </ul>
            <p className="mt-6">To exercise these rights: <strong>privacy@inputperformanceai.com</strong><br />We respond within 30 days.</p>
            <p className="mt-6">You can stop receiving AI coaching messages at any time by disabling chat in Settings.<br />
            This opt-out:</p>
            <ul className="list-disc pl-8 space-y-2">
              <li>Takes effect immediately</li>
              <li>Prevents all automated and AI-generated coaching messages</li>
              <li>Disables all AI coaching reminders</li>
            </ul>
            <p>You can opt back in by re-enabling chat in Settings or by enabling automations within your account dashboard.</p>
          </section>

          {/* 12. AUTOMATED DECISION-MAKING */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">12. Automated Decision-Making & AI</h2>
            <p>Our AI system generates recommendations, insights, nutrition plans, and training programs automatically. These do not have legal or significant effects. You may request human review or deletion at any time.</p>
            <p className="mt-4">Your data is not used to train public AI models.</p>
            <p className="mt-4">Our coaching is delivered through an automated AI system powered by Anthropic (Claude). Messages may be generated using machine learning models and are not written by a human unless explicitly stated. You may request human review or support at any time via email.</p>
            <p className="mt-4">AI-generated training programs use your personal bests, goals, and preferences to calculate workout loading and periodisation. If our AI service is temporarily unavailable, programs may be generated using local algorithms on your device with the same input data. Inaccurate self-reported data (such as personal bests) may result in inappropriate training recommendations.</p>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">Machine Learning & Readiness Scoring</h3>
            <p className="mt-2">In addition to third-party AI providers, we operate our own machine learning service that processes your data to provide:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li><strong>Training Readiness Scores:</strong> A personalised readiness assessment based on your sleep, HRV, recovery score, training load, nutrition compliance, rest days, resting heart rate, and stress levels</li>
              <li><strong>Performance Trend Analysis:</strong> Regression-based analysis of your workout metrics over time to identify progress and plateaus</li>
              <li><strong>Periodisation Recommendations:</strong> Suggestions for training block structure based on your goals and readiness data</li>
              <li><strong>Injury Risk Indicators:</strong> Predictive indicators based on training load patterns, recovery data, and historical trends</li>
            </ul>
            <p className="mt-4">These scores and indicators are generated using statistical models (including z-scores for personalised baselines) and are intended as informational tools only. They do not constitute medical diagnosis or professional assessment. Injury risk indicators are estimates based on available data and should not replace professional medical evaluation.</p>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">AI Providers</h3>
            <p className="mt-2">Our AI features are powered by:</p>
            <ul className="list-disc pl-8 mt-2 space-y-1">
              <li><strong>Anthropic (Claude)</strong> — Nutrition plan generation, dailt summaries, post workout nutrition advice, meal planning, shopping list creation, food image analysis for calorie and macro estimation</li>
            </ul>
            <p className="mt-4">Both providers process your data according to their respective privacy policies and enterprise data processing terms. Your data is not used to train their public models.</p>
            <p className="mt-2">
              <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener" className="text-[#a78bfa] hover:underline">Anthropic Privacy Policy</a> | {" "}
              <a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener" className="text-[#a78bfa] hover:underline">Stripe Privacy Policy</a>
            </p>
          </section>

          {/* 13. CORPORATE CLIENTS */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">13. Corporate Clients</h2>
            <p>For corporate wellbeing programmes:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Reports are anonymised and aggregated</li>
              <li>Employers cannot identify individual employees</li>
              <li>Personal data is never shared without explicit consent</li>
            </ul>
          </section>

          {/* 13A. PARTNER PROGRAM */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">13A. Partner Program</h2>
            <p><strong>If you are a Partner:</strong></p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>We collect your business details for commission payments</li>
              <li>We track referrals and revenue generated through your partner link</li>
              <li>Your branding preferences may be stored if you use custom branding features</li>
              <li>You can view aggregated (anonymised) progress data for your referred clients</li>
              <li>You cannot access individual client health data, messages, or detailed metrics</li>
            </ul>
            <p className="mt-4"><strong>If you are a client referred by a Partner:</strong></p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Your Partner can see that you are an active client</li>
              <li>Your Partner may see limited, aggregated progress data (e.g., engagement level)</li>
              <li>Your Partner cannot access your personal health data, messages, nutrition logs, or detailed metrics</li>
              <li>The partner relationship can be terminated at any time by contacting us</li>
            </ul>
          </section>

          {/* 14. DATA BREACHES */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">14. Data Breaches</h2>
            <p>If a data breach is likely to impact your rights:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>We will notify the ICO within 72 hours (if required)</li>
              <li>We will notify affected individuals without undue delay</li>
            </ul>
          </section>

          {/* 15. AGE POLICY */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">15. Age Policy</h2>
            <p>Our services are not intended for individuals under 16.<br />
            We do not knowingly collect children's data.</p>
          </section>

          {/* 16. CONTACT */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">16. Contact Information</h2>
            <p>For data-related queries:<br />
            <strong>Data Protection Lead:</strong> James Strudwick<br />
            <strong>privacy@inputperformanceai.com</strong><br />
            Input Performance Ltd, 5 Kympton Gardens, GU34 5JL</p>
          </section>

          {/* 17. COMPLAINTS */}
          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">17. Complaints</h2>
            <p>If you believe your data has been mishandled, you may complain to:</p>
            <p>Information Commissioner's Office (ICO)<br />
            Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF<br />
            <a href="https://ico.org.uk" target="_blank" rel="noopener" className="text-[#a78bfa] hover:underline inline-flex items-center gap-1">
              www.ico.org.uk <ExternalLink className="w-4 h-4" />
            </a></p>
          </section>

          {/* FINAL MESSAGE */}
          <div className="mt-16 p-10 bg-gradient-to-r from-[#a78bfa]/10 to-transparent rounded-2xl border border-[#a78bfa]/30 text-center">
            <p className="text-xl font-medium">
              Thank you for trusting Input with your data.
            </p>
            <p className="text-gray-500 mt-4">
              We're committed to your privacy, performance, and peace of mind.
            </p>
          </div>
        </div>
      </main>

      <div className="max-w-5xl mx-auto px-6">
        <div className="bg-amber-900/20 border border-amber-800 rounded-2xl p-6 text-center mb-12">
          <p className="text-amber-300 font-medium">
            Important: Input is not a medical service. All nutrition and training guidance is general and for informational purposes only.
            Always consult a qualified healthcare professional before making changes to your diet, exercise, or health routine.
          </p>
        </div>
      </div>

      <footer className="text-center text-xs text-gray-500 py-8 border-t border-[#e2e8f0] mt-20">
        © {new Date().getFullYear()} Input. All rights reserved.
      </footer>
    </div>
  );
}
